import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, MessageSquare, CalendarCheck, ShieldCheck, Train, Briefcase, GraduationCap,
  HeartPulse, ShoppingBag, Building2,
} from "lucide-react";
import { initLenis } from "../lib/smooth";
import { getSiteSettings, digitsOnly } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/ContactFooter";
import { LeadModal } from "../components/LeadForm";
import { StickyMobileBar, WhatsAppFloat } from "../components/StickyElements";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Reveal, SectionHeading } from "../components/Reveal";
import { IMAGES, AGENT_REG, WA_MESSAGE } from "../data/site";

const VICINITY = [
  { icon: Train, title: "Connectivity", desc: "The nearest metro is indicatively ~12 minutes away, with the airport around 30 minutes from the vicinity." },
  { icon: Briefcase, title: "IT & Employment Hubs", desc: "Infosys ~1 min, ITC Infotech and Wipro ~2 mins, Unitech Infospace ~10 mins and TCS ~12 mins (indicative)." },
  { icon: GraduationCap, title: "Schools & Universities", desc: "Orchids The International School ~7 mins, DPS Newtown ~15 mins, St Xavier's University ~7 mins, Sister Nivedita University ~15 mins." },
  { icon: HeartPulse, title: "Healthcare", desc: "Wellcare Nursing Home under a minute, Tata Medical Centre ~12 mins, Neotia Bhagirathi and Ohio Hospital ~15 mins." },
  { icon: ShoppingBag, title: "Shopping & Conveniences", desc: "Gabtala Bazaar ~1 min, Spencer's ~6 mins, Downtown Mall and Rosedale Plaza ~7 mins each." },
  { icon: Building2, title: "Access to New Town", desc: "Biswa Bangla Gate ~12 mins keeps the heart of New Town within easy reach of the vicinity." },
];

const CHECKLIST = [
  "Exact project location — visit the site, not just the landmark pin",
  "RERA registration of the project and the agent, verified on the WBRERA website",
  "Carpet area, not just super built-up area",
  "Floor plan efficiency and usable room sizes",
  "Natural light and ventilation — open sides matter",
  "Open and green spaces within the project",
  "Amenities your household will genuinely use",
  "Connectivity at your actual commute hours",
  "Developer credentials and delivery history",
  "Complete pricing with applicable additional charges",
  "Current availability for your preferred tower and floor",
];

const V21A_HIGHLIGHTS = [
  "2 & 3 BHK residences across five towers",
  "90% homes with 3-side open views",
  "Double-height sky terraces",
  "Efficient floor plans with reduced circulation space",
  "Vastu-compliant layouts",
  "Natural light and cross ventilation",
  "IGBC Precertified Platinum Rated Green Building",
  "3-acre Central Park",
  "Shuttle service to nearest metro planned",
];

const FLATS_FAQS = [
  {
    q: "Are flats available near New Town Action Area 3?",
    a: "Yes. The Action Area III vicinity is an active residential micro-market. For example, Vinayak 21 Acres at Hatishala — off and adjacent to Newtown Action Area III — offers 2 BHK and 3 BHK residences across five towers.",
  },
  {
    q: "What types of flats are available near Action Area III?",
    a: "Buyers will mostly find 2 BHK and 3 BHK apartments in gated residential projects. At Vinayak 21 Acres, the range spans 2 BHK 2T homes and 3 BHK Smart, Comfort and Luxury configurations.",
  },
  {
    q: "Are 2 BHK flats available near Action Area 3?",
    a: "Yes. Vinayak 21 Acres offers 2 BHK 2T residences of approximately 595–597 sq ft carpet area with about 60 sq ft balcony, 728 sq ft BUA and 971 sq ft SBUA, as Type B and Type G across Towers 1A, 1B, 2A and 2B.",
  },
  {
    q: "Are 3 BHK flats available near Action Area 3?",
    a: "Yes. The project offers three verified 3 BHK families — Smart (approx. 698–703 sq ft carpet), Comfort (approx. 736–783 sq ft) and Luxury (approx. 823–943 sq ft) — across all five towers.",
  },
  {
    q: "Where is Vinayak 21 Acres located?",
    a: "Vinayak 21 Acres is located at Hatishala — off and adjacent to Newtown Action Area III, Kolkata.",
  },
  {
    q: "Is Vinayak 21 Acres inside New Town Action Area 3?",
    a: "Available project information places Vinayak 21 Acres at Hatishala, off/adjacent to Newtown Action Area III — not physically inside Action Area III itself.",
  },
  {
    q: "What configurations are available at Vinayak 21 Acres?",
    a: "2 BHK 2T homes and 3 BHK Smart, Comfort and Luxury layouts — 40 unit variants in total across Towers 1A, 1B, 2A, 2B and 2C, each with an official floor plan available to view.",
  },
  {
    q: "How can I check the latest price and availability?",
    a: "Pricing and availability are subject to change. Contact Forest Hill Realtors through the enquiry buttons on this page, WhatsApp or a call for the latest developer-provided pricing and available inventory.",
  },
  {
    q: "How can I book a site visit?",
    a: "Use any Book Site Visit button on this page, the sticky enquiry bar on mobile, or message us on WhatsApp — Forest Hill Realtors will arrange a convenient slot.",
  },
];

export default function FlatsNearAA3() {
  const [modal, setModal] = useState({ open: false, source: "Flats Near AA3 Page Enquiry", interest: "" });
  const [contact, setContact] = useState({ phone: "+919163841785", whatsapp: "+919163841785" });

  useEffect(() => {
    document.title = "Flats Near New Town Action Area 3 | 2 & 3 BHK Properties";
    const meta = document.querySelector('meta[name="description"]');
    if (meta)
      meta.setAttribute(
        "content",
        "Looking for flats near New Town Action Area 3 Kolkata? Explore 2 & 3 BHK residential options and discover Vinayak 21 Acres at Hatishala, off Action Area III."
      );
    initLenis();
    getSiteSettings().then(setContact);
  }, []);

  const openModal = (source, interest = "") => setModal({ open: true, source, interest });
  const waLink = `https://wa.me/${digitsOnly(contact.whatsapp)}?text=${encodeURIComponent(WA_MESSAGE)}`;

  return (
    <div className="bg-[#FFFFFF]" data-testid="flatsaa3-page">
      <Header onBookVisit={() => openModal("Site Visit Booking")} phone={contact.phone} />
      <main>
        <section className="relative bg-[#EAF5D6] pt-36 pb-16 sm:pt-44 sm:pb-20 overflow-hidden hero-grain">
          <div className="absolute inset-0 opacity-[0.14]" aria-hidden="true">
            <img src={IMAGES.hero} alt="" className="w-full h-full object-cover" loading="eager" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="overline-tag" data-testid="flatsaa3-overline">Locality Guide · Forest Hill Realtors</p>
            <h1
              className="font-serif-display text-[#3F6212] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6 max-w-4xl"
              data-testid="flatsaa3-h1"
            >
              Flats Near New Town <span className="text-[#84CC16]">Action Area 3</span>
            </h1>
            <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg max-w-2xl leading-relaxed">
              A practical guide for buyers searching for flats near Newtown Action Area III — what the vicinity
              offers, what to verify before booking, and verified 2 &amp; 3 BHK residential flats near Action Area III
              Kolkata at Vinayak 21 Acres, Hatishala.
            </p>
            <div className="mt-8">
              <Link to="/" data-testid="flatsaa3-home-link"
                className="inline-flex items-center gap-2 text-[#84CC16] font-sub font-semibold text-sm border border-[#84CC16]/50 rounded-full px-6 py-3 hover:bg-[#84CC16]/10 active:scale-[0.98] transition-all duration-300">
                Explore Vinayak 21 Acres <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </section>

        <section data-testid="flatsaa3-intro-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="01" eyebrow="Start Here" title="Flats Near New Town Action Area 3" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  Searching for property near New Town Action Area 3 usually starts with the job map: the vicinity
                  sits beside New Town's IT corridor, so buyers comparing homes here are often weighing commute time
                  against space and budget. But a short drive to work should not mean compromising on light, air or
                  open space.
                </p>
                <p>
                  This guide covers what the vicinity offers, a due-diligence checklist, and the verified residential
                  options at Vinayak 21 Acres, Hatishala — adjacent to Action Area III.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="flatsaa3-vicinity-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="02" eyebrow="The Locality" title="Why Consider the Action Area III Vicinity?" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
              {VICINITY.map((v, i) => (
                <Reveal key={v.title} delay={0.04 * i}>
                  <div data-testid={`flatsaa3-vicinity-${i}`}
                    className="bg-white border border-[#DCECC0] rounded-xl p-6 h-full hover:border-[#84CC16] hover:-translate-y-1 hover:shadow-[0_20px_40px_-18px_rgba(77,124,15,0.18)] transition-all duration-500">
                    <div className="w-11 h-11 rounded-full bg-[#EAF5D6] border border-[#84CC16]/30 flex items-center justify-center text-[#365314]">
                      <v.icon size={19} strokeWidth={1.6} />
                    </div>
                    <h3 className="font-serif-display text-xl text-[#365314] mt-4 leading-snug">{v.title}</h3>
                    <p className="text-sm text-[#65A30D] mt-2 leading-relaxed">{v.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <p className="text-xs text-[#65A30D] mt-8">Travel times are indicative and may vary depending on traffic and route.</p>
            </Reveal>
          </div>
        </section>

        <section data-testid="flatsaa3-checklist-section" className="py-16 sm:py-24 bg-[#EAF5D6]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="03" eyebrow="Due Diligence" title="What to Check Before Buying a Flat Near Action Area 3" dark />
            <Reveal delay={0.15}>
              <ul className="mt-10 space-y-3.5 max-w-3xl">
                {CHECKLIST.map((c, i) => (
                  <li key={c} data-testid={`flatsaa3-check-${i}`} className="flex items-start gap-3.5 text-[#3F6212] text-sm sm:text-base leading-relaxed">
                    <span className="font-mono-spec text-[#84CC16] text-xs mt-1 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    {c}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>

        <section data-testid="flatsaa3-2bhk-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="04" eyebrow="2 BHK" title="2 BHK Flats Near Action Area 3" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  For a 2 BHK near Action Area 3 New Town, buyers typically prioritise an efficient plan, a usable
                  balcony, good ventilation and a short commute. At Vinayak 21 Acres, the verified 2 BHK 2T layout
                  offers approximately 595–597 sq ft carpet area with about 60 sq ft balcony, 728 sq ft BUA and 971
                  sq ft SBUA — available as Type B and Type G across Towers 1A, 1B, 2A and 2B.
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <Link to="/2-bhk-flats-new-town-kolkata" data-testid="flatsaa3-2bhk-link"
                    className="inline-flex items-center gap-2 text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                    2 BHK Flats in New Town Kolkata <ArrowRight size={15} />
                  </Link>
                  <button data-testid="flatsaa3-check-2bhk-btn" onClick={() => openModal("Availability Enquiry: 2 BHK", "2 BHK")}
                    className="bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-3.5 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    Check Availability
                  </button>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="flatsaa3-3bhk-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="05" eyebrow="3 BHK" title="3 BHK Flats Near Action Area 3" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  For a 3 BHK near Action Area 3 New Town, compare carpet area, toilet count, terrace provision and
                  usable room sizes rather than headline SBUA. Vinayak 21 Acres offers multiple verified 3 BHK
                  categories — <strong className="text-[#365314]">Smart</strong> (approx. 698–703 sq ft carpet),{" "}
                  <strong className="text-[#365314]">Comfort</strong> (approx. 736–783 sq ft) and{" "}
                  <strong className="text-[#365314]">Luxury</strong> (approx. 823–943 sq ft) — across five towers.
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <Link to="/3-bhk-flats-new-town-kolkata" data-testid="flatsaa3-3bhk-link"
                    className="inline-flex items-center gap-2 text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                    3 BHK Flats in New Town Kolkata <ArrowRight size={15} />
                  </Link>
                  <button data-testid="flatsaa3-check-3bhk-btn" onClick={() => openModal("Availability Enquiry: 3 BHK", "3 BHK")}
                    className="bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-3.5 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    Check Availability
                  </button>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="flatsaa3-project-section" className="py-16 sm:py-24 bg-[#EAF5D6]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="06" eyebrow="One Option Nearby" title="Vinayak 21 Acres Near Newtown Action Area III" dark />
            <Reveal delay={0.15}>
              <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg leading-relaxed max-w-3xl">
                Among residential flats near Action Area III Kolkata, Vinayak 21 Acres is one relevant option to
                evaluate. Vinayak 21 Acres is located at Hatishala, off/adjacent to Newtown Action Area III, Kolkata —
                not physically inside Action Area III itself. Verified highlights from the project's published
                information include:
              </p>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3.5 mt-8">
              {V21A_HIGHLIGHTS.map((f, i) => (
                <Reveal key={f} delay={0.03 * i}>
                  <div data-testid={`flatsaa3-highlight-${i}`} className="flex items-start gap-3 border border-[#3F6212]/12 rounded-xl px-5 py-4 bg-[#3F6212]/[0.04]">
                    <span className="text-[#84CC16] mt-0.5">✦</span>
                    <p className="text-sm text-[#3F6212] leading-relaxed">{f}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="flatsaa3-location-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="07" eyebrow="Connectivity" title="Location & Connectivity" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  From the project vicinity, indicative travel times include: Infosys about 1 minute, ITC Infotech and
                  Wipro about 2 minutes, TCS about 12 minutes, the nearest metro about 12 minutes, Tata Medical Centre
                  about 12 minutes, Orchids The International School about 7 minutes, DPS Newtown about 15 minutes,
                  St Xavier's University about 7 minutes, Spencer's about 6 minutes, and Downtown Mall about 7 minutes.
                </p>
                <p className="text-sm text-[#65A30D]">
                  Travel times are indicative and may vary depending on traffic and route.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="flatsaa3-price-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="08" eyebrow="Pricing" title="Price & Availability" />
            <Reveal delay={0.15}>
              <div className="bg-white border border-[#DCECC0] rounded-2xl p-7 sm:p-10 mt-8">
                <p className="text-[#4D7C0F] text-base sm:text-lg leading-relaxed" data-testid="flatsaa3-price-note">
                  Pricing and availability are subject to change. Contact Forest Hill Realtors for the latest
                  developer-provided pricing and available inventory.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button data-testid="flatsaa3-get-price-btn" onClick={() => openModal("Get Latest Price")}
                    className="bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-3.5 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    Get Latest Price
                  </button>
                  <Link to="/vinayak-21-acres-price" data-testid="flatsaa3-price-link"
                    className="inline-flex items-center gap-2 text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                    Vinayak 21 Acres Price <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="flatsaa3-floorplans-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <SectionHeading index="09" eyebrow="Layouts" title="Floor Plans" center />
            <Reveal delay={0.15}>
              <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
                View all 40 official floor plans with verified areas, or dive into the dedicated 2 BHK and 3 BHK
                guides.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link to="/vinayak-21-acres-floor-plan" data-testid="flatsaa3-floorplan-link"
                  className="inline-flex items-center gap-2 bg-[#84CC16] text-[#365314] font-sub font-bold text-sm px-8 py-4 rounded-full hover:bg-[#9ADB3E] active:scale-[0.98] transition-all duration-300">
                  Vinayak 21 Acres Floor Plans <ArrowRight size={15} />
                </Link>
                <Link to="/properties-in-new-town-action-area-3" data-testid="flatsaa3-aa3-link"
                  className="inline-flex items-center gap-2 text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                  Properties in New Town Action Area 3 <ArrowRight size={15} />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="flatsaa3-visit-section" className="pb-16 sm:pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="relative overflow-hidden rounded-2xl bg-[#EAF5D6] p-8 sm:p-12 text-center hero-grain">
                <p className="overline-tag">10 · Visit</p>
                <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3F6212] leading-tight mt-4">
                  Book a Site Visit
                </h2>
                <p className="text-[#4D7C0F] text-sm sm:text-base mt-4 max-w-xl mx-auto leading-relaxed">
                  The surest way to judge a flat near Action Area III is to see it — the commute, the light, the
                  ventilation and the 3-acre central park.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center max-w-2xl mx-auto">
                  <button data-testid="flatsaa3-book-visit-btn" onClick={() => openModal("Site Visit Booking")}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    <CalendarCheck size={16} /> Book a Site Visit
                  </button>
                  <a data-testid="flatsaa3-whatsapp-btn" href={waLink} target="_blank" rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-sub font-bold text-sm px-7 py-4 rounded-full hover:bg-[#1EBE57] active:scale-[0.98] transition-all duration-300">
                    <MessageSquare size={16} /> WhatsApp Us
                  </a>
                </div>
                <div className="mt-6 flex flex-wrap justify-center gap-x-8 gap-y-2">
                  <Link to="/best-property-in-new-town-kolkata" data-testid="flatsaa3-guide-link"
                    className="inline-flex items-center gap-2 text-[#84CC16] text-sm font-sub font-medium hover:underline">
                    Best Property in New Town Kolkata <ArrowRight size={14} />
                  </Link>
                  <Link to="/gated-community-properties-new-town-kolkata" data-testid="flatsaa3-gated-link"
                    className="inline-flex items-center gap-2 text-[#84CC16] text-sm font-sub font-medium hover:underline">
                    Gated Community Properties in New Town Kolkata <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="flatsaa3-faq-section" className="pb-16 sm:pb-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="11" eyebrow="FAQs" title="Flats Near Action Area 3 FAQs" center />
            <Reveal delay={0.15}>
              <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="flatsaa3-faq-accordion">
                {FLATS_FAQS.map((f, i) => (
                  <AccordionItem key={f.q} value={f.q} data-testid={`flatsaa3-faq-item-${i}`}
                    className="bg-white border border-[#DCECC0] rounded-xl px-6 data-[state=open]:border-[#84CC16] transition-colors duration-300">
                    <AccordionTrigger data-testid={`flatsaa3-faq-trigger-${i}`}
                      className="text-left font-sub font-medium text-[15px] text-[#365314] hover:no-underline py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 text-sm text-[#4D7C0F] leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>

        <div className="bg-[#EAF5D6] border-t border-[#DCECC0] py-7" data-testid="flatsaa3-partner-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
            <ShieldCheck size={17} className="text-[#84CC16] shrink-0" />
            <p className="text-[#4D7C0F] text-xs sm:text-[13px] leading-relaxed">
              <span className="text-[#3F6212] font-sub font-semibold">Forest Hill Realtors</span> · Authorised Channel
              Partner · WBRERA Agent Registration: <span className="font-mono-spec">{AGENT_REG}</span>
            </p>
          </div>
        </div>

        <Footer />
      </main>
      <LeadModal open={modal.open} onClose={() => setModal((m) => ({ ...m, open: false }))} source={modal.source} defaultInterest={modal.interest} whatsapp={contact.whatsapp} />
      <StickyMobileBar phone={contact.phone} whatsapp={contact.whatsapp} onEnquire={() => openModal("Quick Enquiry")} />
      <WhatsAppFloat whatsapp={contact.whatsapp} />
    </div>
  );
}
